import React, { useEffect, useState } from "react";
import { Box, Button, Typography, styled, keyframes } from "@mui/material";
import { CalculatingFit } from "./CalculatingFit";
import { CenterPanelContainer } from "./styles";
import { COLUMN_HEIGHT } from "../../constants";
import { FlipIcon } from "../icons/FlipIcon";
import { SparkleIcon } from "../icons/SparkleIcon";
import type { MSHatData } from "../../types";
import type { FacetDefinition } from "../../../components/FacetInfoModal";
import {
  HAT_HIGH_INTEREST_THRESHOLD,
  HAT_LOW_INTEREST_THRESHOLD,
  HAT_MIN_VALID_SCORE,
} from "../../../constants/interestThresholds";

interface BackCenterPanelProps {
  hat: MSHatData;
  onFlip: () => void;
  cmsFacets?: FacetDefinition[];
  userHighInterestRiasec?: string[];
  userLowInterestRiasec?: string[];
  userTopWorkValues?: string[];
  /** When true, render fit results without showing the "See Your Fit" button or calculating animation. */
  fitRevealed?: boolean;
  /** Fires once the calculating animation completes (i.e. the user just revealed). */
  onFitRevealed?: () => void;
  clusterColor?: string;
}

// --- Animations ---

const sparkle = keyframes`
  0%, 100% { opacity: 0; transform: scale(0) rotate(0deg); }
  20% { opacity: 1; transform: scale(1) rotate(0deg); }
  50% { opacity: 1; transform: scale(2) rotate(20deg); }
  80% { opacity: 0.6; transform: scale(0.8) rotate(-10deg); }
`;

const twinkle = keyframes`
  0%, 100% { opacity: 0.4; transform: scale(0.6); }
  50% { opacity: 1; transform: scale(1); }
`;

const glowPulse = keyframes`
  0%, 100% { box-shadow: 0 0 6px #fa680d; }
  50% { box-shadow: 0 0 12px #fa7c0d, 0 0 4px #FFD54F; }
`;

// --- Styled components ---

const CardContainer = styled(Box, {
  label: "BackCenterCard",
  shouldForwardProp: (prop) => prop !== "borderColor",
})<{ borderColor?: string }>(({ borderColor }) => ({
  position: "relative",
  width: "100%",
  height: "100%",
  borderRadius: 16,
  overflow: "hidden",
  backgroundColor: "rgba(145, 158, 171, 0.2)",
  backdropFilter: "blur(30px)",
  border: "4px solid transparent",
  transition: "border-color 0.2s ease",
  ...(borderColor && {
    "&:has(.BackCenterFlipButton-root:hover)": {
      borderColor,
    },
  }),
}));

// Outer: positions and centers horizontally. Inner: scales.
// Splitting avoids a Firefox bug with translateX(-50%) + scale(calc(...)) combined.
const CardContentPositioner = styled(Box, {
  label: "BackCenterCardContentPositioner",
})(() => ({
  position: "absolute",
  top: 0,
  left: "50%",
  transform: "translateX(-50%)",
}));

const CardContent = styled(Box, {
  label: "BackCenterCardContent",
})(() => ({
  width: 368,
  height: 569,
  display: "flex",
  flexDirection: "column",
  padding: "16px 24px",
  gap: 16,
  alignItems: "center",
  transform: `scale(calc(${COLUMN_HEIGHT} / 569px))`,
  transformOrigin: "top center",
}));

const CardTitle = styled(Typography, {
  label: "BackCenterTitle",
})(() => ({
  fontFamily: "Proxima Nova, sans-serif",
  fontWeight: 600,
  fontSize: "18px",
  lineHeight: "28px",
  color: "#ffffff",
  textAlign: "center",
  width: "100%",
}));

const SectionLabel = styled(Typography, {
  label: "BackCenterSectionLabel",
})(() => ({
  fontFamily: "Proxima Nova, sans-serif",
  fontWeight: 600,
  fontSize: "14px",
  lineHeight: "22px",
  color: "#ffffff",
  textTransform: "uppercase",
  width: "100%",
}));

const CoreGrid = styled(Box, {
  label: "BackCenterCoreGrid",
})(() => ({
  display: "flex",
  flexDirection: "column",
  gap: 8,
  width: "100%",
}));

const CoreRow = styled(Box, {
  label: "BackCenterCoreRow",
})(() => ({
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: 24,
  width: "100%",
}));

const CoreItem = styled(Box, {
  label: "BackCenterCoreItem",
  shouldForwardProp: (prop) => prop !== "isHigh" && prop !== "facetColor",
})<{ isHigh?: boolean; facetColor?: string }>(({ isHigh, facetColor }) => ({
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: "6px 8px",
  borderRadius: 8,
  flex: 1,
  backgroundColor: isHigh ? facetColor || "#c684ff" : "#c4cdd5",
}));

const CoreIcon = styled(Box, {
  label: "BackCenterCoreIcon",
  shouldForwardProp: (prop) => prop !== "isMatch",
})<{ isMatch?: boolean }>(({ isMatch }) => ({
  width: 48,
  height: 48,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  position: "relative",
  borderRadius: "50%",
  ...(isMatch && {
    animation: `${glowPulse} 2s ease-in-out infinite`,
    "&::before": {
      content: '""',
      position: "absolute",
      inset: -3,
      borderRadius: "50%",
      backgroundColor: "#F9AD5A",
      zIndex: 0,
    },
  }),
  "& img": {
    width: 48,
    height: 48,
    objectFit: "contain",
    position: "relative",
    zIndex: 1,
  },
}));

const CoreInfo = styled(Box, {
  label: "BackCenterCoreInfo",
})(() => ({
  display: "flex",
  flexDirection: "column",
}));

const InterestLevel = styled(Typography, {
  label: "BackCenterInterestLevel",
})(() => ({
  fontFamily: "Proxima Nova, sans-serif",
  fontWeight: 600,
  fontSize: "12px",
  lineHeight: "18px",
  color: "#1c252e",
}));

const DimensionName = styled(Typography, {
  label: "BackCenterDimensionName",
})(() => ({
  fontFamily: "Proxima Nova, sans-serif",
  fontWeight: 600,
  fontSize: "14px",
  lineHeight: "22px",
  color: "rgba(0, 0, 0, 0.8)",
}));

const WorkValuesRow = styled(Box, {
  label: "BackCenterWorkValuesRow",
})(() => ({
  display: "flex",
  gap: 24,
  width: "100%",
}));

const WorkValueItem = styled(Box, {
  label: "BackCenterWorkValueItem",
})(() => ({
  display: "flex",
  alignItems: "center",
  gap: 8,
  justifyContent: "center",
  position: "relative",
}));

const WorkValueIconContainer = styled(Box, {
  label: "BackCenterWorkValueIcon",
  shouldForwardProp: (prop) => prop !== "isMatch",
})<{ isMatch?: boolean }>(({ isMatch }) => ({
  width: 48,
  height: 48,
  borderRadius: "100%",
  backgroundColor: "#c684ff",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  position: "relative",
  ...(isMatch && {
    animation: `${glowPulse} 2s ease-in-out infinite`,
    "&::before": {
      content: '""',
      position: "absolute",
      inset: -3,
      borderRadius: "100%",
      backgroundColor: "#F9AD5A",
      zIndex: 0,
    },
  }),
  "& img": {
    width: 48,
    height: 48,
    objectFit: "contain",
    borderRadius: "100%",
    position: "relative",
    zIndex: 1,
  },
}));

const WorkValueName = styled(Typography, {
  label: "BackCenterWorkValueName",
})(() => ({
  fontFamily: "Proxima Nova, sans-serif",
  fontWeight: 400,
  fontSize: "12px",
  lineHeight: "18px",
  color: "#ffffff",
}));

const MatchText = styled(Typography, {
  label: "BackCenterMatchText",
})(() => ({
  fontFamily: "Proxima Nova, sans-serif",
  fontStyle: "italic",
  fontWeight: 400,
  fontSize: "14px",
  lineHeight: "22px",
  color: "#fff5cc",
  textAlign: "center",
}));

const SeeYourFitButton = styled(Button, {
  label: "BackCenterSeeYourFit",
})(() => ({
  color: "#ffffff",
  borderColor: "rgba(255, 255, 255, 0.32)",
  fontFamily: "Proxima Nova, sans-serif",
  fontWeight: 600,
  fontSize: "14px",
  textTransform: "none",
  borderRadius: "8px",
  paddingLeft: 24,
  paddingRight: 24,
  "&:hover": {
    borderColor: "rgba(255, 255, 255, 0.48)",
    backgroundColor: "rgba(255, 255, 255, 0.08)",
  },
}));

const FlipButton = styled("button", {
  label: "BackCenterFlipButton",
})(() => ({
  border: "none",
  padding: 0,
  outline: "none",
  "&:focus-visible": {
    outline: "2px solid #fa680d",
    outlineOffset: 2,
  },
  position: "absolute",
  bottom: 24,
  left: "50%",
  transform: "translateX(-50%)",
  width: 64,
  height: 64,
  borderRadius: "50%",
  backgroundColor: "rgba(255, 255, 255, 0.32)",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  transition: "background-color 0.2s ease",
  "&:hover": {
    backgroundColor: "rgba(255, 255, 255, 0.48)",
  },
}));

FlipButton.defaultProps = { className: "BackCenterFlipButton-root" };

const FlipLabel = styled(Typography, {
  label: "BackCenterFlipLabel",
})(() => ({
  fontFamily: "Proxima Nova, sans-serif",
  fontWeight: 600,
  fontSize: "16px",
  lineHeight: "24px",
  color: "#ffffff",
  textAlign: "center",
}));

// Sparkle stars for matched work values
const SparkleContainer = styled(Box, {
  label: "SparkleContainer",
})(() => ({
  position: "absolute",
  inset: "-12px",
  pointerEvents: "none",
  zIndex: 2,
}));

const Star = styled("svg", {
  label: "SparkleStar",
})<{ delay: number; x: string; y: string }>(({ delay, x, y }) => ({
  position: "absolute",
  left: x,
  top: y,
  filter: "drop-shadow(0 0 1px rgb(236, 236, 236))",
  animation: `${sparkle} 2.4s ${delay}s ease-in-out infinite`,
}));

const Dot = styled(Box, {
  label: "SparkleDot",
})<{ delay: number; x: string; y: string }>(({ delay, x, y }) => ({
  position: "absolute",
  left: x,
  top: y,
  width: 3,
  height: 3,
  borderRadius: "50%",
  backgroundColor: "#fa830d",
  boxShadow: "0 0 2px rgba(0, 0, 0, 0.4)",
  animation: `${twinkle} 1.8s ${delay}s ease-in-out infinite`,
}));

const Sparkles = () => (
  <SparkleContainer>
    <Star delay={0} x="0" y="20%" width="10" height="10" viewBox="0 0 10 10">
      <path d="M5 0L6 4L10 5L6 6L5 10L4 6L0 5L4 4Z" fill="#fa830d" />
    </Star>
    <Star delay={0.8} x="75%" y="0" width="8" height="8" viewBox="0 0 10 10">
      <path d="M5 0L6 4L10 5L6 6L5 10L4 6L0 5L4 4Z" fill="#fa830d" />
    </Star>
    <Star delay={1.6} x="85%" y="70%" width="7" height="7" viewBox="0 0 10 10">
      <path d="M5 0L6 4L10 5L6 6L5 10L4 6L0 5L4 4Z" fill="#fa830d" />
    </Star>
    <Dot delay={0.4} x="15%" y="75%" />
    <Dot delay={1.2} x="60%" y="85%" />
  </SparkleContainer>
);

// --- Data mappings ---

const RIASEC_SCORE_TO_FACET: Record<string, string> = {
  realistic: "RIASEC:R",
  investigative: "RIASEC:I",
  artistic: "RIASEC:A",
  social: "RIASEC:S",
  enterprising: "RIASEC:E",
  conventional: "RIASEC:C",
};

const WORK_VALUE_SCORE_TO_FACET: Record<string, string> = {
  achievement: "Accomplishing Goals",
  independence: "Being Independent",
  recognition: "Getting Recognition",
  relationships: "Building Relationships",
  support: "Trust & Support",
  workingConditions: "Stability & Security",
};

const RIASEC_KEYS = Object.keys(RIASEC_SCORE_TO_FACET);
const WORK_VALUE_KEYS = Object.keys(WORK_VALUE_SCORE_TO_FACET);

export const BackCenterPanel = ({
  hat,
  onFlip,
  cmsFacets = [],
  userHighInterestRiasec,
  userLowInterestRiasec,
  userTopWorkValues,
  fitRevealed = false,
  onFitRevealed,
  clusterColor,
}: BackCenterPanelProps) => {
  const [showFit, setShowFit] = useState(fitRevealed);
  const [calculating, setCalculating] = useState(false);

  // Sync with the cached reveal flag if it loads after the initial render.
  useEffect(() => {
    if (fitRevealed) setShowFit(true);
  }, [fitRevealed]);

  const handleSeeYourFit = () => {
    setCalculating(true);
  };

  const handleCalculatingComplete = () => {
    setCalculating(false);
    setShowFit(true);
    onFitRevealed?.();
  };

  const facetMap = new Map(cmsFacets.map((f) => [f.facetCode, f]));

  // Show every RIASEC facet whose hat score crosses the high or low threshold.
  // Scores below 1 indicate missing data and are skipped entirely.
  const allRiasecEntries = RIASEC_KEYS.map((key) => ({
    key,
    score: hat.riasecScores?.[key] || 0,
    facet: facetMap.get(RIASEC_SCORE_TO_FACET[key]),
  }));

  const highRiasecEntries = allRiasecEntries
    .filter(
      (e) =>
        e.score >= HAT_HIGH_INTEREST_THRESHOLD &&
        e.score >= HAT_MIN_VALID_SCORE,
    )
    .sort((a, b) => b.score - a.score);
  const lowRiasecEntries = allRiasecEntries
    .filter(
      (e) =>
        e.score <= HAT_LOW_INTEREST_THRESHOLD && e.score >= HAT_MIN_VALID_SCORE,
    )
    .sort((a, b) => a.score - b.score);
  const riasecEntries: Array<
    (typeof allRiasecEntries)[number] & { isHigh: boolean }
  > = [
    ...highRiasecEntries.map((e) => ({ ...e, isHigh: true })),
    ...lowRiasecEntries.map((e) => ({ ...e, isHigh: false })),
  ];

  // Top 2 work values for the hat
  const workValueEntries = WORK_VALUE_KEYS.map((key) => ({
    key,
    score: hat.workValues?.[key] || 0,
    facet: facetMap.get(WORK_VALUE_SCORE_TO_FACET[key]),
  }))
    .sort((a, b) => b.score - a.score)
    .slice(0, 2);

  // Compute matches (only used after "See Your Fit" is clicked).
  // A core tile matches when the hat and user share the same tier (both high or both low).
  const userHighRiasecSet = new Set(userHighInterestRiasec || []);
  const userLowRiasecSet = new Set(userLowInterestRiasec || []);
  const userWorkValueSet = new Set(userTopWorkValues || []);

  const riasecMatchSet = new Set(
    riasecEntries
      .filter((e) =>
        e.isHigh ? userHighRiasecSet.has(e.key) : userLowRiasecSet.has(e.key),
      )
      .map((e) => e.key),
  );
  const hatTopWorkValueKeys = workValueEntries.map((e) => e.key);
  const workValueMatches = hatTopWorkValueKeys.filter((k) =>
    userWorkValueSet.has(k),
  );
  const workValueMatchSet = new Set(workValueMatches);

  const totalMatches = riasecMatchSet.size + workValueMatches.length;
  const totalItems = riasecEntries.length + hatTopWorkValueKeys.length;

  const matchNames: string[] = [
    ...riasecEntries
      .filter((e) => riasecMatchSet.has(e.key))
      .map((e) => e.facet?.variable || e.key),
    ...workValueMatches.map((k) => {
      const entry = workValueEntries.find((e) => e.key === k);
      return entry?.facet?.variable || entry?.facet?.longLabel || k;
    }),
  ];

  const hasUserData =
    (userHighInterestRiasec && userHighInterestRiasec.length > 0) ||
    (userLowInterestRiasec && userLowInterestRiasec.length > 0) ||
    (userTopWorkValues && userTopWorkValues.length > 0);

  return (
    <CenterPanelContainer>
      <CardContainer borderColor={clusterColor}>
        <CardContentPositioner>
          <CardContent>
            <CardTitle>Hat Profile</CardTitle>

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
                width: "100%",
              }}
            >
              <SectionLabel>Core</SectionLabel>
              <CoreGrid>
                {Array.from(
                  { length: Math.ceil(riasecEntries.length / 2) },
                  (_, rowIdx) => rowIdx * 2,
                ).map((startIdx) => (
                  <CoreRow key={startIdx}>
                    {riasecEntries.slice(startIdx, startIdx + 2).map((dim) => {
                      const isHigh = dim.isHigh;
                      const isMatch = showFit && riasecMatchSet.has(dim.key);
                      const label = dim.facet?.variable || dim.key;
                      return (
                        <CoreItem
                          key={dim.key}
                          isHigh={isHigh}
                          facetColor={dim.facet?.secondaryColor}
                        >
                          <CoreIcon isMatch={isMatch}>
                            {dim.facet?.icon ? (
                              <img
                                src={dim.facet.icon}
                                alt={label}
                                style={
                                  !isHigh
                                    ? { filter: "grayscale(100%)" }
                                    : undefined
                                }
                              />
                            ) : (
                              <Typography
                                sx={{ fontSize: "20px", color: "#1c252e" }}
                              >
                                {label.charAt(0).toUpperCase()}
                              </Typography>
                            )}
                            {isMatch && <Sparkles />}
                          </CoreIcon>
                          <CoreInfo>
                            <InterestLevel>
                              {isHigh ? "High Interest" : "Low Interest"}
                            </InterestLevel>
                            <DimensionName>{label}</DimensionName>
                          </CoreInfo>
                        </CoreItem>
                      );
                    })}
                  </CoreRow>
                ))}
              </CoreGrid>
            </Box>

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
                width: "100%",
                flex: 1,
              }}
            >
              <SectionLabel>Work Values</SectionLabel>
              <WorkValuesRow>
                {workValueEntries.map((wv) => {
                  const label =
                    wv.facet?.variable || wv.facet?.longLabel || wv.key;
                  const isMatch = showFit && workValueMatchSet.has(wv.key);
                  return (
                    <WorkValueItem key={wv.key}>
                      <WorkValueIconContainer isMatch={isMatch}>
                        {wv.facet?.icon ? (
                          <img src={wv.facet.icon} alt={label} />
                        ) : (
                          <Typography sx={{ fontSize: "16px", color: "#fff" }}>
                            {label.charAt(0).toUpperCase()}
                          </Typography>
                        )}
                        {isMatch && <Sparkles />}
                      </WorkValueIconContainer>
                      <WorkValueName>{label}</WorkValueName>
                    </WorkValueItem>
                  );
                })}
              </WorkValuesRow>

              {calculating ? (
                <CalculatingFit
                  matchNames={matchNames}
                  onComplete={handleCalculatingComplete}
                />
              ) : showFit && hasUserData ? (
                <Box sx={{ mt: 12, textAlign: "center" }}>
                  <MatchText>
                    You have {totalMatches} out of {totalItems} matches ✨
                  </MatchText>
                </Box>
              ) : hasUserData ? (
                <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
                  <SeeYourFitButton
                    variant="outlined"
                    onClick={handleSeeYourFit}
                    startIcon={<SparkleIcon />}
                  >
                    See Your Fit
                  </SeeYourFitButton>
                </Box>
              ) : null}
            </Box>

            <FlipButton onClick={onFlip} aria-label="Flip card to front">
              <FlipIcon />
              <FlipLabel>Flip</FlipLabel>
            </FlipButton>
          </CardContent>
        </CardContentPositioner>
      </CardContainer>
    </CenterPanelContainer>
  );
};
